<template>
  <div ref="chart" :style="{width, height}"></div>
</template>

<script>
import echarts from 'echarts';

const keyToTime = (key) => {
  const str = `${key}:00`;
  return (new Date(str)).getTime();
};

const DynamicEnvChart = {
  props: {
    // 单个数据系列 {"2019-01-04 19:00": 9.17, "2019-01-04 19:05": 15.15}
    // 或多个数据系列 {"2019-01-04 19:00": [9.17, 20.00], "2019-01-04 19:05": [15.15, 12.50]}
    electricCurrents: {
      type: [Object, Array],
      default: null
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    yAxisName: { // 纵坐标名称
      type: String,
      default: ''
    },
    seriesNames: { // 电量,上行流量,下行流量
      type: String,
      default: ''
    },
    legendNamesMap: {
      type: Object, // 非必须，如果存在则在图例中替换原系列名显示，如 {"上行流量":"动环信息","下行流量":"动环信息"}
      default: null
    },
    zoomBar: { // 是否使用缩放条组件
      type: Boolean,
      default: false
    },
    color: {
      type: Array,
      default () {
        return ['#2aafff', '#2C68FF', '#61a0a8', '#d48265', '#91c7ae', '#749f83',
          '#ca8622', '#bda29a', '#6e7074', '#546570', '#c4ccd3'];
      }
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    unit: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      chart: null
    };
  },
  computed: {
    seriesNamesArr () {
      return this.seriesNames.split(',');
    }
  },
  mounted () {
    this.renderChart(this.electricCurrents);

    window.onresize = () => (() => {
      this.chart.resize();
    })();
  },
  watch: {
    electricCurrents (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.renderChart(newVal);
      }
    }
  }, // end of watch
  methods: {
    renderChart (data) {
      this.chart = echarts.init(this.$refs.chart);
      this.chart.clear();

      if (!data || (Array.isArray(data) && !data.length)) {
        return;
      }

      // 按时间排序先
      const sortedArr = Object.keys(data)
        .sort((a, b) => (keyToTime(a) - keyToTime(b)))
        .map((key) => {
          let v = data[key];
          if (!v) {
            v = [];
          }
          if (!Array.isArray(v)) { // 把单系列也统一处理成数组形式
            v = [v];
          }
          return {
            time: key,
            value: v.map(vnum => Number(vnum).toFixed(2))
          };
        });
      const keys = sortedArr.map(item => item.time); // 一个 key 字符串数组
      const values = sortedArr.map(item => item.value); // 一个二维数组
      // console.log(222, keys, values, this.isEmpty);

      if (!keys.length || !values.length) return;

      const isLargeData = keys.length > 500;

      // 按系列的个数形成各自的数据
      const series = [];
      for (let i = 0; i < values[0].length; i++) {
        series.push({
          name: this.seriesNamesArr[i],
          data: values.map(subarr => subarr[i]),
          type: 'line',
          large: isLargeData
        });
      }

      // 可选的图例名称替换
      const legendNameFormatter = this.legendNamesMap
        ? (serieName) => { // 每次调用的参数为单个系列名
          if (serieName in this.legendNamesMap) {
            return this.legendNamesMap[serieName];
          }
          return serieName;
        }
        : null;

      // 缩放设置
      const zoomArr = [{ type: 'inside' }];
      if (this.zoomBar) {
        zoomArr.push({
          type: 'slider',
          bottom: 30
        });
      }

      const cfg = {
        dataZoom: zoomArr,
        xAxis: {
          type: 'category',
          axisLine: {
            lineStyle: {
              color: '#E9E9E9'
            }
          },
          boundaryGap: !this.zoomBar,
          axisLabel: {
            textStyle: {
              color: 'rgba(0,0,0,.65)'
            },
            showMaxLabel: true,
          },
          axisTick: {
            inside: this.zoomBar,
          },
          data: keys
        },
        yAxis: {
          name: this.yAxisName,
          type: 'value',
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#E9E9E9'
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: !this.zoomBar,
          },
          axisLabel: {
            textStyle: {
              color: 'rgba(0,0,0,.65)'
            }
          }
        },
        series,
        legend: {
          data: this.seriesNamesArr,
          bottom: 0,
          selectedMode: false,
          formatter: legendNameFormatter
        },
        color: this.color,
        tooltip: {
          trigger: 'axis',
          formatter: (paramsArr) => {
            let cont = `${paramsArr[0].name}<br/>`;
            cont += paramsArr.map(
              params => `${params.marker}${params.seriesName}: ${params.value}${this.unit}`
            ).join('<br/>');
            return cont;
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(123,122,122,0.65)'
            }
          }
        },
        backgroundColor: '#fff',
        grid: {
          left: this.zoomBar ? '8%' : '5%',
          right: this.zoomBar ? '8%' : '5%',
          top: '15%',
          bottom: this.zoomBar ? '20%' : '15%'
        }
      };

      if (this.isEmpty) {
        cfg.yAxis.min = 0;
        cfg.yAxis.max = 100;
        cfg.dataZoom = null;
        cfg.tooltip = null;
      }

      this.chart.setOption(cfg);
    }
  } // end of methods
};

export default DynamicEnvChart;
</script>



// WEBPACK FOOTER //
// src/components/common/DynamicEnvChart.vue